import { ErrorHandler, EnvironmentProviders, makeEnvironmentProviders, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { provideCacheableAnimationLoader, provideLottieOptions } from 'ngx-lottie';

import { WINDOW_TOKEN } from './services/window.token';
import { OVERLAY_FACTORY, OverlayFactoryService } from './components/overlay/overlay-factory.service';
import { SIDE_SHEET_SELECTOR_SCROLL_STRATEGY_PROVIDER } from './components/side-sheet-selector/side-sheet-selector.domain';
import { MODAL_SCROLL_STRATEGY_FACTORY, MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY } from './components/modal/modal-scroll-strategy-factory';
import { MODAL_CONTAINER } from './components/modal/modal.domain';
import { ModalWindowComponent } from './components/modal/components/modal-window/modal-window.component';
import { TimezoneService } from './services/date/timezone.service';
import { DateFormatterService } from './services/date/date-formatter.service';
import { DateParserService } from './services/date/date-parser.service';
import { DateCodecService } from './services/date/date-codec.service';
import { ErrorHandlerService } from './services/error-handler.service';
import { ProvincesStatesCodesService } from './services/provinces-states/provinces-states-codes.service';
import { CurrencyPipe } from './pipes/currency.pipe';
import { DatePipe } from './pipes/date.pipe';
import { NumberPipe } from './pipes/number.pipe';
import { PercentPipe } from './pipes/percent.pipe';
import { PhonePipe } from './pipes/phone.pipe';
import { AnalyticsEventKeyPipe } from './pipes/analytics-event-key.pipe';
import { AnalyticsDatePipe } from './pipes/analytics-date.pipe';
// RestModule providers
import { RestInterpolatorService } from './services/rest/rest-interpolator.service';
import { RestJsonCodecService } from './services/rest/rest-json-codec.service';
import { RestErrorHandlerService, REST_ERROR_HANDLERS } from './services/rest/rest-error-handler.service';
import { RestSystemErrorHandlerService } from './services/rest/rest-system-error-handler.service';
import { restActivityInterceptor } from './services/rest/interceptors/rest-activity-interceptor';
import { restSessionInterceptor } from './services/rest/interceptors/rest-session-interceptor';
import { restXsrfInterceptor } from './services/rest/interceptors/rest-xsrf-interceptor';
// StorageModule providers
import { SessionStorageService } from './services/storage/session-storage.service';
import { LocalStorageService } from './services/storage/local-storage.service';
// DataSourceModule providers
import { CachingDataSourceFactory } from './services/datatable/caching-data-source';
// HelpLinkModule providers
import { HelpLinkArticleResolverService } from './components/help-link/help-link-article-resolver.service';
import { HELP_LINK_ARTICLE_RESOLVER } from './components/help-link/help-link.domain';
// DateRangeSelectorModule providers
import { DatePickerService } from './components/date-range-selector/date-picker/date-picker.service';
import { DatePickerConfig } from './components/date-range-selector/date-picker/date-picker.config';
import { RangeSelectorCalendarConfig } from './components/date-range-selector/range-selector-calendar/range-selector-calendar.config';
// FormModule providers
import { FormAccessibilityService } from './components/form/accessibility-alert/form-accessibility.service';
// NgxDatatable providers (ScrollbarHelper requires @SkipSelf — must be in module/environment injector)
import { ScrollbarHelper } from './components/datatable/services/scrollbar-helper.service';
import { DimensionsHelper } from './components/datatable/services/dimensions-helper.service';
import { ColumnChangesService } from './components/datatable/services/column-changes.service';

function corePlayerFactory(): Promise<typeof import('lottie-web/build/player/lottie_svg')> {
  return import(/* webpackChunkName: 'lottie-web' */ 'lottie-web/build/player/lottie_svg');
}

const playerFactory = corePlayerFactory;

/**
 * Provides all services and configuration from the core library.
 * Use this in `ApplicationConfig.providers` instead of importing `CoreModule`.
 */
export function provideCore(): EnvironmentProviders {
  return makeEnvironmentProviders([
    // Lottie animation
    provideLottieOptions({ player: playerFactory }),
    provideCacheableAnimationLoader(),

    // Overlay / modal providers (previously in ComponentsModule)
    {
      provide: OVERLAY_FACTORY,
      useExisting: OverlayFactoryService
    },
    SIDE_SHEET_SELECTOR_SCROLL_STRATEGY_PROVIDER,
    {
      provide: WINDOW_TOKEN,
      useValue: window
    },
    {
      provide: MODAL_SCROLL_STRATEGY_FACTORY,
      useFactory: MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY,
      deps: [DOCUMENT, WINDOW_TOKEN, RendererFactory2, ScrollStrategyOptions]
    },
    {
      provide: MODAL_CONTAINER,
      useValue: ModalWindowComponent
    },

    // Services (previously in ServicesModule)
    TimezoneService,
    ProvincesStatesCodesService,
    DateFormatterService,
    DateCodecService,
    DateParserService,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    provideHttpClient(withXhr(), withInterceptors([restActivityInterceptor, restSessionInterceptor, restXsrfInterceptor])),

    // Pipes that need DI (previously in PipesModule)
    CurrencyPipe,
    DatePipe,
    NumberPipe,
    PercentPipe,
    PhonePipe,
    AnalyticsEventKeyPipe,
    AnalyticsDatePipe,

    // RestModule providers (previously in ServicesModule → RestModule)
    RestInterpolatorService,
    RestJsonCodecService,
    RestErrorHandlerService,
    { provide: REST_ERROR_HANDLERS, useClass: RestSystemErrorHandlerService, multi: true },

    // StorageModule providers (previously in ServicesModule → StorageModule)
    SessionStorageService,
    LocalStorageService,

    // DataSourceModule providers (previously in ServicesModule → DataSourceModule)
    CachingDataSourceFactory,

    // HelpLinkModule providers
    HelpLinkArticleResolverService,
    { provide: HELP_LINK_ARTICLE_RESOLVER, useExisting: HelpLinkArticleResolverService },

    // DateRangeSelectorModule providers
    DatePickerService,
    DatePickerConfig,
    RangeSelectorCalendarConfig,

    // FormModule providers
    FormAccessibilityService,

    // NgxDatatable providers — ScrollbarHelper uses @SkipSelf so must be in env injector
    ScrollbarHelper,
    DimensionsHelper,
    ColumnChangesService
  ]);
}

